// RigSentinel Context Engine — Phase RCE-1
//
// SESSION SWITCHER (modal)
// ------------------------
// Floating modal that lets the user pick the active Rig / Well / BHA /
// Drill String / Hole Section / Operation. Mounted once at the app root;
// listens for the `rce:open-session-switcher` window event dispatched by
// the Active Operation Pill.

import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useEngineeringSession } from "@/lib/rce/useEngineeringSession";
import { useStackMode } from "@/lib/stackModeContext";
import { listWellProfiles } from "@/lib/well/storage";
import { listRigSheets }    from "@/lib/rigsheet/storage";
import { listBhasForWell }  from "@/lib/bha/storage";
import { listDrillStringsForWell } from "@/lib/drillstring/storage";
import { listProjects }     from "@/lib/rce/project";
import { listMudProgramsForWell } from "@/lib/mud/program";

const OPERATION_OPTIONS = [
  "", "Drilling Ahead", "Tripping In", "Tripping Out", "Reaming",
  "Circulating", "Cementing", "Running Casing", "BOP Test",
  "Wireline Logging", "Well Control", "Other",
];

const HOLE_SECTION_OPTIONS = [
  "", "Conductor", "Surface", "Intermediate", "Production", "Liner", "Tieback", "Other",
];

// A well belongs to Subsea mode only when it's genuinely offshore with a
// subsea BOP — mirrors WellProfileHub's rule and the Save-time coercion in
// WellProfileEditor.
function _wellStackMode(w) {
  return (w?.environment === "offshore" && w?.bopLocation === "subsea") ? "subsea" : "surface";
}

export default function SessionSwitcher() {
  const { session, patch, clear } = useEngineeringSession();
  const { stackMode } = useStackMode();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("rce:open-session-switcher", handler);
    return () => window.removeEventListener("rce:open-session-switcher", handler);
  }, []);

  const [draft, setDraft] = useState(() => session || {});
  useEffect(() => { if (open) setDraft(session || {}); }, [open, session]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  // Surface/Subsea isolation — only offer wells that belong to the current
  // operating mode, so this switcher can't be used to bind a Subsea session
  // to an existing Surface well (or vice versa).
  const wells = safeList(listWellProfiles).filter((w) => _wellStackMode(w) === stackMode);
  const rigs  = safeList(listRigSheets);
  const projects = safeList(listProjects);
  const bhas  = draft.wellId ? safeList(() => listBhasForWell(draft.wellId)) : [];
  const dss   = draft.wellId ? safeList(() => listDrillStringsForWell(draft.wellId)) : [];
  const mudProgs = draft.wellId ? safeList(() => listMudProgramsForWell(draft.wellId)) : [];

  const applyAndClose = () => {
    patch({
      projectId:        draft.projectId        || "",
      rigId:            draft.rigId            || "",
      wellId:           draft.wellId           || "",
      bhaId:            draft.bhaId            || "",
      drillStringId:    draft.drillStringId    || "",
      mudProgramId:     draft.mudProgramId     || "",
      holeSection:      draft.holeSection      || "",
      currentOperation: draft.currentOperation || "",
    });
    setOpen(false);
  };

  return (
    <div
      data-testid="session-switcher"
      className="fixed inset-0 z-[10010] flex items-end sm:items-center justify-center"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full sm:max-w-md rounded-t-xl sm:rounded-xl border border-[var(--rs-border)] shadow-2xl p-4 max-h-[90vh] overflow-y-auto"
        style={{ background: "rgba(16,18,22,0.98)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center mb-3">
          <h2 className="text-base font-heading font-black flex-1">Engineering Session</h2>
          <button
            data-testid="session-switcher-close"
            onClick={() => setOpen(false)}
            className="btn-ghost h-8 w-8 rounded inline-flex items-center justify-center"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[11px] label-up text-[var(--rs-text-3)] mb-3">
          Pick the operational context. Pages use this as the master source of truth — Phase RCE-1.
        </p>

        <Pick
          label="Project"
          value={draft.projectId || ""}
          onChange={(v) => setDraft({ ...draft, projectId: v })}
          testid="ss-project"
          options={[
            { value: "", label: "— None (uncategorised) —" },
            ...projects.map((p) => ({ value: p.id, label: `${p.name} · ${p.operator}` })),
          ]}
        />
        <Pick
          label="Rig"
          value={draft.rigId || ""}
          onChange={(v) => setDraft({ ...draft, rigId: v })}
          testid="ss-rig"
          options={[
            { value: "", label: "— None —" },
            ...rigs.map((r) => ({
              value: r.id,
              label: `${r.payload?.rigName || "Rig"} · ${r.payload?.rigType || ""}`,
            })),
          ]}
        />
        <Pick
          label="Well"
          value={draft.wellId || ""}
          onChange={(v) => setDraft({ ...draft, wellId: v, bhaId: "", drillStringId: "" })}
          testid="ss-well"
          options={[
            { value: "", label: "— None —" },
            ...wells.map((w) => ({
              value: w.id,
              label: `${w.wellName}${w.operator ? " · " + w.operator : ""}`,
            })),
          ]}
        />
        <Pick
          label="BHA"
          value={draft.bhaId || ""}
          onChange={(v) => setDraft({ ...draft, bhaId: v })}
          testid="ss-bha"
          disabled={!draft.wellId}
          options={[
            { value: "", label: draft.wellId ? "— None —" : "Select a well first" },
            ...bhas.map((b) => ({
              value: b.id,
              label: `${b.name || b.bhaNumber || "BHA"}${b.revision ? " · Rev " + b.revision : ""} · ${b.operationalStatus}`,
            })),
          ]}
        />
        <Pick
          label="Drill String"
          value={draft.drillStringId || ""}
          onChange={(v) => setDraft({ ...draft, drillStringId: v })}
          testid="ss-ds"
          disabled={!draft.wellId}
          options={[
            { value: "", label: draft.wellId ? "— None —" : "Select a well first" },
            ...dss.map((d) => ({
              value: d.id,
              label: `${d.name || d.stringNumber || "Drill String"}${d.revision ? " · Rev " + d.revision : ""} · ${d.operationalStatus}`,
            })),
          ]}
        />
        <Pick
          label="Mud Program"
          value={draft.mudProgramId || ""}
          onChange={(v) => setDraft({ ...draft, mudProgramId: v })}
          testid="ss-mudprog"
          disabled={!draft.wellId}
          options={[
            { value: "", label: draft.wellId ? "— Auto (in-use) —" : "Select a well first" },
            ...mudProgs.map((p) => ({
              value: p.id,
              label: `${p.name} · ${p.fluidBasis} · ${p.status}`,
            })),
          ]}
        />
        <Pick
          label="Hole Section"
          value={draft.holeSection || ""}
          onChange={(v) => setDraft({ ...draft, holeSection: v })}
          testid="ss-holesec"
          options={HOLE_SECTION_OPTIONS.map((s) => ({ value: s, label: s || "— None —" }))}
        />
        <Pick
          label="Current Operation"
          value={draft.currentOperation || ""}
          onChange={(v) => setDraft({ ...draft, currentOperation: v })}
          testid="ss-op"
          options={OPERATION_OPTIONS.map((s) => ({ value: s, label: s || "— None —" }))}
        />

        <div className="flex gap-2 mt-4">
          <button
            data-testid="session-switcher-clear"
            onClick={() => { clear(); setOpen(false); }}
            className="btn-ghost h-9 px-3 rounded text-[12px] label-up"
          >
            End Session
          </button>
          <div className="flex-1" />
          <button
            onClick={() => setOpen(false)}
            className="btn-ghost h-9 px-3 rounded text-[12px] label-up"
          >
            Cancel
          </button>
          <button
            data-testid="session-switcher-apply"
            onClick={applyAndClose}
            className="h-9 px-3 rounded text-[12px] label-up font-semibold"
            style={{ background: "var(--rs-amber)", color: "var(--rs-bg)" }}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

function safeList(fn) { try { return fn() || []; } catch { return []; } }

function Pick({ label, value, onChange, options, testid, disabled }) {
  return (
    <label className="block mb-2.5">
      <div className="label-up text-[9.5px] text-[var(--rs-text-3)] mb-1">{label}</div>
      <select
        data-testid={testid}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-9 px-2 rounded border bg-[rgba(22,24,29,0.85)] text-[12px] text-[var(--rs-text-1)] disabled:opacity-40"
        style={{ borderColor: "var(--rs-border)" }}
      >
        {options.map((o) => (
          <option key={`${testid}-${o.value}`} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
